import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import AdSense from '../components/AdSense';

const CONTACT_EMAIL = 'optionview580@gmail.com';

const faqs = [
  {
    q: 'How accurate is the data?',
    a: <>Data is sourced from free sources, not necessarily maintained by NSE, and data <strong>will not be accurate</strong>. While we take care in processing the data, we cannot guarantee 100% accuracy. Occasional errors in  source files or edge cases in the Black-Scholes solver may cause anomalous values. Please do not use this as a reference to make trades. See our disclaimer for full details.</>,
  },
  {
    q: 'How often is data updated?',
    a: <>Data is updated at the end of every trading day, around 9:30 PM IST. On market holidays or NSE data unavailability, no update occurs for that day.</>,
  },
  {
    q: 'Is this data real-time?',
    a: <>No — OptionView uses end-of-day (EOD) data from the previous trading session. The IV you see is based on closing option prices from the last available trading day on the specific source. Going forward, official data partnerships might allow for real-time data, but that is not in the immediate scope.</>,
  },
  {
    q: 'Which symbols are supported?',
    a: <>Since this is a project tool, symbol coverage is sparse. For IV calculator, 50 NIFTY 50 scrips, and 3 major indices are covered. For Option Analysis, we currently started with one scrip, but will continue to add more as the days progress.</>
  },
  {
    q: 'Can I use this for intraday trading?',
    a: <>Absolutely not, lol. This is an educational project/toolkit. <strong>Neither is the data guaranteed to be accurate, nor is this any official vendor to NSE</strong>. Please use these tools as ways to educate yourself only.</>,
  },
  {
    q: 'Is this tool free?',
    a: <>Yes, OptionView is free to use currently. The website is ad-supported (via Google AdSense), if you have ad-blocker installed, I humbly request you to deactivate it, the ad earnings (if any) will help support this project.</>,
  },
  {
    q: 'Do I need to sign up or create an account?',
    a: <>Not currently, and all data is accessible to all users. In the future, login might be required to save strategies, parameters etc., but they will be clearly called out.</>,
  },
  {
    q: 'Does this work on mobile?',
    a: <>Yes — OptionView is fully responsive and works on mobile browsers. The layout adapts to smaller screens. A dedicated mobile app (iOS and Android) is on our roadmap for a future release.</>,
  },
  {
    q: 'What if I find a bug or data error?',
    a: `Please email us at ${CONTACT_EMAIL} with details — the symbol, date, and what you observed. We investigate and fix data issues as quickly as possible. Accurate data is our top priority.`,
  },
  {
    q: 'Can you add more symbols?',
    a: `Yes! We plan to, please reach out to our email, ${CONTACT_EMAIL} with your request.`,
  },
  {
    q: 'Is this tool suitable for beginners?',
    a: `Yes — OptionView is designed with beginners in mind. The interpretations (e.g., "IV is elevated — consider selling premium") translate raw numbers into actionable insights. The How It Works page explains IV, IV Rank, and IV Percentile in plain language with examples. That said, options trading itself carries significant risk, and we recommend learning the basics of options before trading. The tool gives you data; the decision-making requires knowledge and experience.`,
  },
  {
    q: 'Do you provide trading signals or recommendations?',
    a: `No. OptionView is a data and analytics tool, not a SEBI-registered advisory. We do not provide personalised trading signals, recommendations to buy or sell specific options, or portfolio management advice. The IV analysis and strategy hints shown on the site are general educational content only. Please read our full disclaimer before using the tool.`,
  },
];

function FAQItem({ q, a, isOpen, onToggle }) {
  return (
    <div className="bg-surface border border-surface rounded-clay shadow-clay dark:shadow-clay-dark overflow-hidden">
      <button
        className="w-full text-left px-5 py-4 flex justify-between items-start gap-3 hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <span className="font-medium text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">{q}</span>
        <span className="text-neutral-400 dark:text-neutral-500 flex-shrink-0 mt-0.5 text-lg leading-none">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-4 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed border-t border-white/50 dark:border-transparent pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <>
      <SEOHead
        title="FAQ — Implied Volatility, IV Rank & IV Percentile for Indian Options | OptionView"
        description="Frequently asked questions about OptionView's IV calculator — what is implied volatility, how IV Rank and IV Percentile are calculated, data accuracy, supported NSE symbols, and more."
        keywords="IV calculator FAQ, what is implied volatility, IV rank India, IV percentile options, NIFTY options FAQ, Black-Scholes questions, options greeks explained"
        canonicalPath="/faq"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-3">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Back to Home
        </Link>
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">Frequently Asked Questions</h1>
        <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-5">
          Everything you need to know about OptionView and Implied Volatility for Indian markets.
        </p>

        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              q={faq.q}
              a={faq.a}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>

        <div className="mt-10 bg-primary-50 dark:bg-primary-900/20 border border-white/70 dark:border-primary-800/40 rounded-clay shadow-clay dark:shadow-clay-dark p-5 text-center">
          <p className="text-primary-800 dark:text-primary-300 text-sm mb-3">
            Still have a question? We're happy to help.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-clay-sm hover:bg-primary-700 transition-colors shadow-clay-btn"
          >
            Email Us →
          </a>
        </div>

        <div className="mt-5 text-center">
          <Link to="/how-it-works" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            New to IV? Read our How It Works guide →
          </Link>
        </div>

        <AdSense adSlot="" label="FAQ Banner" adStyle={{ minHeight: 90, marginTop: 24 }} />
      </div>
    </>
  );
}
